import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Activity, Phone, Mail, MessageCircle, Calendar, FileText,
  Check, Loader, AlertCircle, Clock, User, Link, Plus, X,
  Filter, Download, TrendingUp, Crosshair, Zap, ShieldCheck,
  ArrowUpRight, ChevronRight, Layers, Info, MoreVertical
} from 'lucide-react';
import { fetchActivities, createActivity } from '../../redux/crmSlice';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatDate';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import { downloadCsv } from '../../utils/exportCsv';
import SimpleGanttChart from '../../components/charts/SimpleGanttChart';

import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
// Must match the Activity schema's real type enum — this previously offered
// 'message', which the schema doesn't accept (it's 'whatsapp_message'), so
// logging a "WhatsApp" activity always failed validation.
const ACTIVITY_TYPES = ['all', 'call', 'email', 'whatsapp_message', 'meeting', 'note', 'status_change'];

const TYPE_CONFIG = {
  call: { Icon: Phone, badge: 'sf-badge-neutral', label: 'Voice Call' },
  email: { Icon: Mail, badge: 'sf-badge-neutral', label: 'Email Outreach' },
  whatsapp_message: { Icon: MessageCircle, badge: 'sf-badge-success', label: 'WhatsApp' },
  meeting: { Icon: Calendar, badge: 'sf-badge-neutral', label: 'Meeting' },
  note: { Icon: FileText, badge: 'sf-badge-warning', label: 'Internal Note' },
  status_change: { Icon: Check, badge: 'sf-badge-success', label: 'Status Update' },
  default: { Icon: Activity, badge: 'sf-badge-neutral', label: 'System Event' },
};

const GANTT_COLORS = {
  call: '#0176D3', email: '#3b82f6', whatsapp_message: '#25D366',
  meeting: '#8b5cf6', note: '#f59e0b', status_change: '#2E844A', default: '#94a3b8',
};

// Only activities with a real span (a scheduled start, and either a duration,
// an explicit end, or a type where a default duration is a reasonable guess)
// are Gantt-able — pure log events (lead_created, stage_change, message logs
// with no scheduling) have no meaningful bar and are excluded rather than
// faked into one.
const DEFAULT_DURATION_TYPES = new Set(['call', 'meeting', 'video']);

function activityToGanttRow(activity) {
  if (!activity.scheduledAt) return null;
  const start = new Date(activity.scheduledAt);
  let end;
  if (activity.completedAt) end = new Date(activity.completedAt);
  else if (activity.duration) end = new Date(start.getTime() + activity.duration * 60000);
  else if (DEFAULT_DURATION_TYPES.has(activity.type)) end = new Date(start.getTime() + 30 * 60000);
  else return null;
  if (end <= start) end = new Date(start.getTime() + 15 * 60000);

  return {
    id: activity._id,
    label: activity.title || activity.type,
    segments: [{
      start, end,
      color: GANTT_COLORS[activity.type] || GANTT_COLORS.default,
      tooltip: `${activity.title || activity.type} — ${start.toLocaleString('en-IN')}`,
    }],
  };
}

export default function ActivitiesPage() {
  const dispatch = useDispatch();
  const { activities, loading } = useSelector(state => state.crm);
  const bulk = useBulkSelection();

  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState('feed');
  const [logModal, setLogModal] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [formData, setFormData] = useState({ type: 'call', title: '', description: '' });

  useEffect(() => {
    dispatch(fetchActivities({ limit: 50 }));
  }, [dispatch]);

  const handleLogActivity = async (e) => {
    e.preventDefault();
    setLogLoading(true);
    try {
      await dispatch(createActivity(formData)).unwrap();
      alertService.success('Success', 'Activity saved');
      setLogModal(false);
      setFormData({ type: 'call', title: '', description: '' });
    } catch (err) {
      alertService.error('Error', 'Failed to add activity');
    } finally {
      setLogLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return activities;
    return activities.filter(a => a.type === typeFilter);
  }, [activities, typeFilter]);

  const ganttRows = useMemo(() => filtered.map(activityToGanttRow).filter(Boolean), [filtered]);

  const handleExportFeed = async () => {
    try {
      const params = typeFilter !== 'all' ? { type: typeFilter } : {};
      const response = await api.get('/crm/activities/export', { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-feed-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export activity feed');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await api.post('/crm/activities/bulk', { activityIds: bulk.selectedIds });
      const count = res.data?.modifiedCount ?? bulk.selectedCount;
      bulk.clear();
      dispatch(fetchActivities({ limit: 50 }));
      alertService.success('Deleted', `${count} activity(ies) deleted`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk delete failed');
    }
  };

  const handleBulkExport = () => {
    const selected = activities.filter(a => bulk.selected.has(a._id));
    downloadCsv(
      'activities-selected.csv',
      ['Type', 'Title', 'Description', 'ScheduledAt', 'CreatedBy', 'CreatedAt'],
      selected.map(a => [a.type, a.title, a.description, a.scheduledAt, a.createdBy, a.createdAt])
    );
  };

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="Activity Feed"
        subtitle="Chronological record of commercial interactions"
        icon={Activity}
        actions={<>
<button onClick={handleExportFeed} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setLogModal(true)} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> Log Activity
          </button>
  </>}
      />

      {/* KPI Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Interactions</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Outbound Rate</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">
            {activities.length ? Math.round((activities.filter(a => a.direction === 'outbound').length / activities.length) * 100) : 0}%
          </h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Inbound Responses</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.filter(a => a.direction === 'inbound').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#DD7A01]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">System Events</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{activities.filter(a => a.createdBy === 'system').length}</h3>
        </div>
      </div>

      {/* Filter Tabs + View Toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit overflow-x-auto no-scrollbar max-w-full">
          {ACTIVITY_TYPES.map(t => (
            <button
              key={t}
              className={cn(
                "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all whitespace-nowrap",
                typeFilter === t ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
              )}
              onClick={() => setTypeFilter(t)}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit">
          {['feed', 'gantt'].map(v => (
            <button
              key={v}
              className={cn(
                "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
                view === v ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
              )}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk selection controls */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={bulk.isAllSelected(filtered.map(a => a._id))}
              onChange={() => bulk.toggleAll(filtered.map(a => a._id))}
              className="w-4 h-4 accent-[#0176D3] cursor-pointer"
            />
            <span className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Select all visible activities</span>
          </label>
        </div>
      )}
      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={handleBulkDelete}
        onExport={handleBulkExport}
        entityLabel="activity"
      />

      {/* Gantt View */}
      {view === 'gantt' && (
        <SimpleGanttChart
          rows={ganttRows}
          emptyMessage="No scheduled calls, meetings, or timed activities to plot yet — activities with no scheduled time (notes, status changes, message logs) aren't shown here."
        />
      )}

      {/* Timeline List */}
      <div className="space-y-6" hidden={view !== 'feed'}>
        {loading && activities.length === 0 ? (
          <div className="py-24 text-center space-y-4">
            <Loader size={32} className="text-[#0176D3] animate-spin mx-auto" />
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#706E6B]">Loading activities…</p>
          </div>
        ) : filtered.length > 0 ? filtered.map(activity => {
          const cfg = TYPE_CONFIG[activity.type] || TYPE_CONFIG.default;
          const Icon = cfg.Icon;

          return (
            <div key={activity._id} className="relative pl-8 group">
              {/* Connector Line */}
              <div className="absolute left-[15px] top-[30px] bottom-[-24px] w-px bg-[#D8DDE6] group-last:hidden" />

              {/* Node Icon */}
              <div className={cn(
                "absolute left-0 top-0 w-8 h-8 rounded-full border border-white shadow-sm flex items-center justify-center z-10 transition-transform group-hover:scale-110",
                activity.type === 'call' ? 'bg-[#0176D3] text-white' :
                activity.type === 'status_change' ? 'bg-[#2E844A] text-white' : 'bg-[#F3F2F2] text-[#706E6B]'
              )}>
                <Icon size={14} />
              </div>

              <div className={cn(
                "sf-card p-5 group-hover:border-[#0176D3] transition-all",
                bulk.selected.has(activity._id) && "ring-2 ring-[#0176D3]"
              )}>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={bulk.selected.has(activity._id)}
                    onChange={() => bulk.toggle(activity._id)}
                    aria-label={`Select ${activity.title}`}
                    className="w-4 h-4 accent-[#0176D3] cursor-pointer shrink-0"
                  />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[14px] font-bold text-[#080707] uppercase tracking-tight">{activity.title}</h4>
                  <span className={cn("sf-badge", cfg.badge)}>{cfg.label}</span>
                </div>
                <p className="text-[13px] text-[#706E6B] leading-relaxed mb-4">{activity.description}</p>
                
                <div className="flex items-center justify-between pt-4 border-t border-[#D8DDE6]">
                  <div className="flex items-center gap-4">
                    {activity.leadName && (
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#0176D3] uppercase tracking-widest cursor-pointer hover:underline">
                        <User size={12} /> {activity.leadName}
                      </div>
                    )}
                    {activity.dealTitle && (
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#2E844A] uppercase tracking-widest cursor-pointer hover:underline">
                        <Layers size={12} /> {activity.dealTitle}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">
                    <Clock size={12} /> {formatDateTime(activity.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="sf-card py-32 text-center border-dashed border-2 opacity-50">
            <Activity className="mx-auto mb-4 opacity-20" size={48} />
            <p className="text-sm font-bold text-[#706E6B] uppercase tracking-widest">Timeline Vacant</p>
          </div>
        )}
      </div>

      {/* Log Activity Modal */}
      {logModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setLogModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Log Interaction</h3>
              <button onClick={() => setLogModal(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleLogActivity} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Activity Title</label>
                <input required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Channel / Type</label>
                <select value={formData.type} onChange={e => setFormData(p => ({ ...p, type: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                  {ACTIVITY_TYPES.filter(t => t !== 'all').map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Summary</label>
                <textarea required value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} className="w-full min-h-[100px] p-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all resize-none focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setLogModal(false)} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={logLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {logLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
